import React from "react";
import { motion } from "framer-motion";
import {
  Building2,
  Target,
  Users,
  TrendingUp,
  Mail,
  Phone,
  MapPin,
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";

const About = () => {
  const navigate = useNavigate();

  const values = [
    {
      icon: Building2,
      title: "Excelencia",
      description:
        "Comprometidos con ofrecer las mejores propiedades del mercado",
    },
    {
      icon: Users,
      title: "Transparencia",
      description: "Información clara y completa en cada transacción",
    },
    {
      icon: Target,
      title: "Innovación",
      description: "Tecnología de vanguardia para facilitar tu búsqueda",
    },
    {
      icon: TrendingUp,
      title: "Crecimiento",
      description: "Expandiendo horizontes en el mercado inmobiliario",
    },
  ];

  return (
    <div className="min-h-screen">
      <Header />

      <section className="bg-gradient-to-br from-primary/10 to-secondary/10 py-16 md:py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1
              className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 md:mb-6 tracking-tight"
              style={{ fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}
              data-testid="about-title"
            >
              Sobre Metal Concrete
            </h1>
            <p className="text-base md:text-lg lg:text-xl text-muted-foreground leading-relaxed px-4">
              Somos la plataforma líder en México que conecta desarrolladores
              inmobiliarios, propietarios y compradores en un ecosistema digital
              innovador.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-12 md:py-16 lg:py-24 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center mb-16 md:mb-24">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h2
                className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 md:mb-6 tracking-tight"
                style={{ fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}
              >
                Nuestra Misión
              </h2>
              <p className="text-sm md:text-base text-muted-foreground leading-relaxed mb-4">
                En Metal Concrete, una empresa de Suivos, nuestra misión es
                revolucionar la forma en que las personas encuentran y
                comercializan propiedades en México. Combinamos tecnología de
                punta con un profundo conocimiento del mercado inmobiliario.
              </p>
              <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                Creemos que cada persona merece encontrar la propiedad perfecta,
                y cada desarrollador merece una plataforma que potencie su
                alcance y visibilidad.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="rounded-lg overflow-hidden"
            >
              <img
                src="https://images.pexels.com/photos/323780/pexels-photo-323780.jpeg"
                alt="Equipo de SUIVOS"
                className="w-full h-64 md:h-96 object-cover rounded-lg"
              />
            </motion.div>
          </div>

          <div className="mb-16 md:mb-24">
            <h2
              className="text-2xl sm:text-3xl md:text-4xl font-bold mb-8 md:mb-12 text-center tracking-tight"
              style={{ fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}
            >
              Nuestros Valores
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
              {values.map((value, index) => {
                const Icon = value.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="bg-background border border-border/40 rounded-lg p-6 md:p-8 text-center"
                  >
                    <div className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                      <Icon className="w-7 h-7 md:w-8 md:h-8 text-primary" />
                    </div>
                    <h3
                      className="text-lg md:text-xl font-bold mb-2"
                      style={{ fontFamily: "Manrope, sans-serif" }}
                    >
                      {value.title}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {value.description}
                    </p>
                  </motion.div>
                );
              })}
            </div>
          </div>

          <div className="bg-muted rounded-lg p-6 md:p-12">
            <div className="text-center mb-8 md:mb-12">
              <h2
                className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 tracking-tight"
                style={{ fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}
              >
                Contacto
              </h2>
              <p className="text-sm md:text-base text-muted-foreground">
                ¿Tienes preguntas? Estamos aquí para ayudarte
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 max-w-4xl mx-auto">
              <div className="flex flex-col items-center text-center p-4">
                <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-primary/10 flex items-center justify-center mb-3">
                  <Mail className="w-6 h-6 md:w-7 md:h-7 text-primary" />
                </div>
                <h4
                  className="font-bold mb-2"
                  style={{ fontFamily: "Manrope, sans-serif" }}
                >
                  Email
                </h4>
                <p className="text-sm text-muted-foreground">
                  soporte@metalconcrete.com
                </p>
              </div>

              <div className="flex flex-col items-center text-center p-4">
                <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-primary/10 flex items-center justify-center mb-3">
                  <MapPin className="w-6 h-6 md:w-7 md:h-7 text-primary" />
                </div>
                <h4
                  className="font-bold mb-2"
                  style={{ fontFamily: "Manrope, sans-serif" }}
                >
                  Ubicación
                </h4>
                <p className="text-sm text-muted-foreground">
                  Monterrey, MX | Saltillo, MX
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16 lg:py-24 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-primary text-primary-foreground rounded-lg p-8 md:p-12 text-center">
            <h2
              className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 tracking-tight"
              style={{ fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}
            >
              ¿Listo para unirte a nosotros?
            </h2>
            <p className="text-base md:text-lg mb-6 md:mb-8 opacity-90 max-w-2xl mx-auto">
              Comienza a listar tus propiedades hoy mismo y alcanza a miles de
              compradores potenciales
            </p>
            <Button
              size="lg"
              variant="secondary"
              onClick={() => navigate("/register")}
              className="w-full sm:w-auto"
              data-testid="cta-register"
            >
              Registrarse Ahora
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default About;
